/**
 * Row context for every list in the list-builder defaults registry.
 *
 * A list's "row context" is what a single ROW of that list represents. It is
 * the discriminator the list-configuration builder uses to decide which custom
 * components are worth offering when someone configures a column: Cart
 * components belong on cart lines, Shop components on a shop row, Client
 * components on a client, and none of them belong on the others.
 *
 * Why a hand-maintained map rather than deriving it from the configuration:
 * `ListBuilderConfiguration.objectType` is populated on only a handful of
 * configs, and `objects[0].objectTable` is NOT reliably the row entity — the
 * client item-history list leads with `invoice`, the trip list leads with
 * `route`, and the shop lists lead with `item` even though a shop row is a
 * different thing from an item row. Guessing from either source produces
 * confidently wrong answers, so the mapping is stated explicitly.
 *
 * Context values are mostly the row's table name. The exceptions are the
 * lists whose rows carry a meaning their table does not capture — the three
 * shop lists and the four cart lists all sit on `item` / `order_detail` — so
 * those lists are their own context.
 *
 * Lives in libs/core, next to `componentFieldRequirements`, so the same
 * mapping is available to anything that reasons about lists without pulling
 * in React.
 */

/**
 * Every row context a list can have. Declared as a const tuple so component
 * registrations get a compile-time error on a typo instead of silently
 * falling into the "other components" bucket.
 */
export const LIST_ROW_CONTEXTS = [
	'access_log',
	'automation_batch',
	'automation_flow',
	'automation_report',
	'automation_target_list',
	'automation_trigger',
	'call_history',
	'cart_suggestion',
	'client',
	'client_cart',
	'client_category_insight',
	'client_contact',
	'client_ecommerce_user',
	'client_item_history',
	'client_list',
	'client_location',
	'client_shop',
	'client_visit',
	'sales_trip_stop',
	'competitive_price',
	'conversation_agent',
	'conversation_analyzer',
	'conversation_session',
	'conversation_tool',
	'conversation_voice_profile',
	'driver',
	'ecommerce_user',
	'email',
	'email_template',
	'file',
	'group',
	'invoice',
	'invoice_cart',
	'invoice_detail',
	'invoice_shop',
	'issue',
	'item',
	'item_client_history',
	'item_list',
	'item_lot',
	'item_warehouse',
	'license',
	'license_audit',
	'license_disciplinary',
	'license_discrepancy',
	'license_match',
	'license_registry',
	// PD-16211 — a row is a customer plus the products they replenish, not
	// a client row: the quantity cells belong here and nowhere else.
	'replenishment',
	'location',
	'location_item',
	'login_history',
	'merchandiser',
	'merchandiser_template',
	'messaging_template',
	'note',
	'opportunity',
	'order',
	'order_detail',
	'ownership',
	'payment_method',
	'payment_schedule',
	'payment_schedule_occurrence',
	'pdf_template',
	'price_contract',
	'promo',
	'purchase_order',
	'purchase_order_detail',
	'purchase_order_receipt',
	'quote',
	'quote_cart',
	'quote_detail',
	'quote_shop',
	'route',
	'sensor_device',
	'sensor_reading',
	'sent_email',
	'sourcing_request',
	'statement',
	'task',
	'team',
	'transaction',
	'trip',
	'user',
	'vehicle',
	'vendor',
	'vendor_item',
	'warehouse',
] as const;

export type ListRowContext = (typeof LIST_ROW_CONTEXTS)[number];

/**
 * List key (as registered in the list-builder defaults) to the context of a
 * single row in that list.
 *
 * Embedded lists follow the `<row entity>-<parent entity>-embedded` naming
 * convention, so `task-client-embedded` (tasks shown on a client) has a `task`
 * context, not a `client` one.
 *
 * `listRowContexts.test.ts` asserts this map stays complete, so a new list
 * cannot be added without giving it a context.
 */
export const LIST_KEY_ROW_CONTEXTS: Record<string, ListRowContext> = {
	access_logs: 'access_log',
	'automation-reports-batch-embedded': 'automation_report',
	'automation-reports-trigger-embedded': 'automation_report',
	'automation-target-lists-embedded': 'automation_target_list',
	automation_batches: 'automation_batch',
	automation_flows: 'automation_flow',
	automation_reports: 'automation_report',
	automation_target_lists: 'automation_target_list',
	automation_triggers: 'automation_trigger',
	'call-history-vendor-embedded': 'call_history',
	call_history: 'call_history',
	cart_suggestions: 'cart_suggestion',
	client: 'client',
	'client-children-embedded': 'client',
	'client-contact': 'client_contact',
	'client-contacts-embedded': 'client_contact',
	'client-emails-embedded': 'email',
	'client-files-embedded': 'file',
	'client-ownership-embedded': 'ownership',
	client_cart: 'client_cart',
	client_ecommerce_user: 'client_ecommerce_user',
	client_licenses: 'license',
	client_list: 'client_list',
	client_list_clients: 'client',
	client_location: 'client_location',
	client_notes: 'note',
	client_shop: 'client_shop',
	// Rows are trips the client appears on, so trip components apply here even
	// though the list reads from a dedicated history view.
	client_trip_history: 'trip',
	client_visits: 'client_visit',
	// PD-16162 — a Schedule row is one stop on one sales trip.
	sales_trip_schedule: 'sales_trip_stop',
	clientCategoryInsights: 'client_category_insight',
	clientItemHistory: 'client_item_history',
	'competitive-price-client-embedded': 'competitive_price',
	'competitive-price-item-embedded': 'competitive_price',
	'conversation-agent-embedded': 'conversation_session',
	conversation_agent: 'conversation_agent',
	conversation_analyzer: 'conversation_analyzer',
	conversation_session: 'conversation_session',
	conversation_tool: 'conversation_tool',
	conversation_voice_profile: 'conversation_voice_profile',
	driver: 'driver',
	ecommerce_user: 'ecommerce_user',
	ecommerce_user_clients: 'client',
	email_templates: 'email_template',
	group: 'group',
	'group-users': 'user',
	invoice: 'invoice',
	'invoice-client-embedded': 'invoice',
	'invoice-order-embedded': 'invoice',
	invoice_cart: 'invoice_cart',
	invoice_detail: 'invoice_detail',
	invoice_expansion_lines: 'invoice_detail',
	invoice_shop: 'invoice_shop',
	issue: 'issue',
	'issue-client-embedded': 'issue',
	'issue-files-embedded': 'file',
	'issue-link-embedded': 'issue',
	'issue-merchandiser-embedded': 'issue',
	'issue-sourcing-request-embedded': 'issue',
	'issue-vehicle-embedded': 'issue',
	'issue-vendor-embedded': 'issue',
	issue_notes: 'note',
	item: 'item',
	'item-files-embedded': 'file',
	'item-purchased-together-embedded': 'item',
	'item-warehouse-embedded': 'item_warehouse',
	item_list: 'item_list',
	item_list_items: 'item',
	itemClientHistory: 'item_client_history',
	'itemLot-item-embedded': 'item_lot',
	'itemLot-location-embedded': 'item_lot',
	'itemLot-warehouse-embedded': 'item_lot',
	'itemWarehouse-item-embedded': 'item_warehouse',
	lead_eligibility: 'user',
	license: 'license',
	// PD-16252: the compliance attention lists are client_license rows.
	license_compliance: 'license',
	'license-audit-embedded': 'license_audit',
	'license-disciplinary-embedded': 'license_disciplinary',
	'license-discrepancies-embedded': 'license_discrepancy',
	'license-files-embedded': 'file',
	'license-links-embedded': 'license',
	license_match: 'license_match',
	// PD-16593: a row is one decision on a match (Recent Changes).
	license_match_changes: 'license_match',
	license_notes: 'note',
	location: 'location',
	locationItem: 'location_item',
	login_history: 'login_history',
	merchandiser: 'merchandiser',
	merchandiser_template: 'merchandiser_template',
	messaging_templates: 'messaging_template',
	opportunity: 'opportunity',
	'opportunity-client-embedded': 'opportunity',
	'opportunity-files-embedded': 'file',
	opportunity_notes: 'note',
	order: 'order',
	'order-client-embedded': 'order',
	'order-deliveries-received': 'order',
	'order-invoice-embedded': 'order',
	order_detail: 'order_detail',
	order_expansion_lines: 'order_detail',
	ownership: 'ownership',
	ownership_client: 'client',
	payment_methods: 'payment_method',
	payment_schedule_occurrences: 'payment_schedule_occurrence',
	payment_schedules: 'payment_schedule',
	pdf_template: 'pdf_template',
	'price-contract-client-embedded': 'price_contract',
	'price-contract-item-embedded': 'price_contract',
	price_contract: 'price_contract',
	price_contract_client: 'client',
	price_contract_item: 'item',
	promo: 'promo',
	'purchase-order-item-embedded': 'purchase_order',
	'purchase-order-receipt-files-embedded': 'file',
	'purchase-order-vendor-embedded': 'purchase_order',
	purchase_order: 'purchase_order',
	purchase_order_detail: 'purchase_order_detail',
	purchase_order_receipt: 'purchase_order_receipt',
	purchase_order_receipts_main: 'purchase_order_receipt',
	purchase_order_receipts_vendor_embedded: 'purchase_order_receipt',
	quote: 'quote',
	'quote-client-embedded': 'quote',
	quote_cart: 'quote_cart',
	quote_expansion_lines: 'quote_detail',
	quote_shop: 'quote_shop',
	registry: 'license_registry',
	replenishment: 'replenishment',
	route: 'route',
	'sensorReading-device-embedded': 'sensor_reading',
	'sensorReading-sensor-embedded': 'sensor_reading',
	sensor_device: 'sensor_device',
	'sourcing-request-client-embedded': 'sourcing_request',
	'sourcing-request-files-embedded': 'file',
	'sourcing-request-item-embedded': 'sourcing_request',
	'sourcing-request-vendor-embedded': 'sourcing_request',
	sourcing_request: 'sourcing_request',
	sourcing_request_notes: 'note',
	statement: 'statement',
	'statement-client-embedded': 'statement',
	system_sent_emails: 'sent_email',
	task: 'task',
	'task-calendar-modal': 'task',
	'task-client-embedded': 'task',
	'task-issue-embedded': 'task',
	'task-license-embedded': 'task',
	'task-opportunity-embedded': 'task',
	'task-sourcing-request-embedded': 'task',
	team: 'team',
	'transaction-client-embedded': 'transaction',
	'transaction-invoice-embedded': 'transaction',
	'transaction-order-embedded': 'transaction',
	transaction_summary: 'transaction',
	trip: 'trip',
	vehicle: 'vehicle',
	vendor: 'vendor',
	vendor_item: 'vendor_item',
	warehouse: 'warehouse',
};

/**
 * Resolve the row context for a list key.
 *
 * Returns `undefined` for a list key with no entry, which callers must treat
 * as "context unknown" — the picker then falls back to offering every custom
 * component, so an unmapped list is cluttered rather than crippled.
 */
export function getListRowContext(
	listKey: string | undefined,
): ListRowContext | undefined {
	if (listKey == null || listKey === '') {
		return undefined;
	}

	return LIST_KEY_ROW_CONTEXTS[listKey];
}
